import { useMutation, useQueryClient } from '@tanstack/react-query';
import { plantApi } from '../lib/plantApi';
import { CreatePlantInput } from '../types/plant';

export function useCreatePlant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plantData: CreatePlantInput) => plantApi.createPlant(plantData),
    onSuccess: () => {
      // Invalidate all plant-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['myPlants'] });
      queryClient.invalidateQueries({ queryKey: ['plants'] });
    },
  });
}

export function useUpdatePlant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreatePlantInput> }) =>
      plantApi.updatePlant(id, updates),
    onSuccess: updatedPlant => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ['myPlants'] });

      // Update the specific plant in cache (optimistic update)
      queryClient.setQueryData(['plant', updatedPlant.id], updatedPlant);
    },
  });
}

export function useDeletePlant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plantId: string) => plantApi.deletePlant(plantId),
    onSuccess: (_, plantId) => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ['myPlants'] });

      // Remove the specific plant from cache
      queryClient.removeQueries({ queryKey: ['plant', plantId] });
    },
  });
}

export function useGenerateQRCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plantId: string) => plantApi.generateQRCode(plantId),
    onSuccess: data => {
      // Update the plant in cache with new QR code data
      queryClient.setQueryData(['plant', data.plant.id], data.plant);

      // Also invalidate the list to ensure it reflects QR code status
      queryClient.invalidateQueries({ queryKey: ['myPlants'] });
    },
  });
}
